/**
 * PR-A2 (ADO 286302): the reserved-height QA answer slot.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx so the routed full-page
 * surface can reuse the layout contract rather than reimplement it. Nothing
 * here knows whether it is rendering inside a dropdown or a page — the only
 * surface-specific value is the reserved height, which is why
 * `QA_SLOT_HEIGHT_PX` is exported (the page re-derives it for its own width).
 *
 * graft/A7 v2 (Rapid QA): the same slot hosts the AI Overview (RapidAnswer).
 * The layout contract is unchanged in spirit — the slot is reserved at a
 * fixed height and the overview only ever changes CONTENT inside it — with
 * two v2 rules:
 *
 *  1. WHEN the slot is owed. Only for an overview-shaped query (a question,
 *     a quantity, a status set, a superlative, a dated ask): the driver's
 *     `armed` says the LIVE input is one, and the frame is reserved from
 *     that moment — before any request, before any result paints — so the
 *     later arrival of the prose cannot move the list. A lookup owes no
 *     slot at all: no strip, no facts line, nothing.
 *  2. WHAT happens when there is nothing to say. An overview that ends
 *     without prose (declined, no results, error) collapses the frame to
 *     zero with a 220 ms ease — once, at stream end, never mid-stream. That
 *     is a deliberate departure from the legacy rule of holding the height
 *     for the rest of the search: a Google-style overview that has nothing
 *     to add does not leave a labelled empty box above the results. The
 *     reasons the agent dock can do better on keep a one-line note instead.
 *
 * The legacy `qa` event path still renders when Rapid QA is inert (server
 * flag off / route absent / v1 builder).
 */
import { useEffect, useState } from "react";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import { AiSearchQA } from "./AiSearchQA";
import {
    type IRapidAnswerSource,
    type IRapidAnswerState,
    RAPID_ANSWER_IDLE,
} from "./hooks/useRapidAnswer";
import { RapidAnswer, rapidOutcome } from "./RapidAnswer";
import type { AiSearchPhase, IQAAnswer } from "./types";

/**
 * PR-A2 (ADO 286302): fixed pixel height of the reserved QA answer slot. The
 * slot renders at this height the moment intent reports isQuestion (skeleton
 * card) and keeps it when the qa event replaces the skeleton with the answer
 * — so the arrival of the answer can never change the y-position of the
 * result list below it. Inline style, not a class, so the layout contract is
 * assertable in the DOM-level tests.
 *
 * graft/A6: raised 132 → 156 after measuring the real states in a browser
 * (label + 2 clamped lines + source + the expand toggle = 151-153px on both
 * surfaces; the 2-line clamp pins the line count regardless of width).
 */
export const QA_SLOT_HEIGHT_PX = 156;

/**
 * graft/A7 v2: the overview CARD — head (20) + three lines of prose (72) +
 * the sources/actions footer (26) + paddings and border — measures ~172px
 * on both surfaces (the 3-line clamp pins the line count); 184 leaves
 * headroom for a wrapped footer. The NOTE is one line: 56.
 */
export const RAPID_QA_CARD_HEIGHT_PX = 184;
export const RAPID_QA_NOTE_HEIGHT_PX = 56;

/**
 * A2-review (thread 1511891): answers longer than this line-clamp inside the
 * fixed slot and get a "Show full answer" toggle instead of clipping behind
 * an undiscoverable nested scrollbar. Expanding is a user-initiated layout
 * change (the slot grows to fit), which D4 permits — only PASSIVE arrivals
 * may never move painted content. ~3 clamp lines of answer text.
 */
export const QA_CLAMP_THRESHOLD_CHARS = 220;

/** graft/A7 v2: three lines of overview prose at the panel's width. */
export const RAPID_QA_CLAMP_THRESHOLD_CHARS = 260;

/**
 * PR-A2: placeholder rendered inside the reserved QA slot between the intent
 * event (isQuestion) and the qa event. Same card chrome as the real answer so
 * the swap is a content change, not a layout change.
 */
function QASkeletonCard() {
    return (
        <div className="ai-search-qa" data-testid="ai-search-qa-skeleton">
            <div className="ai-search-qa-card">
                <div className="ai-search-qa-label">AI Answer</div>
                <div className="ai-search-skeleton-lines">
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--medium" />
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--short" />
                </div>
            </div>
        </div>
    );
}

interface IAiSearchQASlotProps {
    qaAnswers: Record<string, IQAAnswer>;
    phase: AiSearchPhase;
    isQuestion: boolean;
    hasQuery: boolean;
    /** Resets the expansion toggle when a new search starts. */
    searchId: string;
    /** Reserved height in pixels for the LEGACY answer. Surfaces override for their own width. */
    heightPx?: number;
    /**
     * graft/A7: the Rapid QA stream. Owns the slot when it is active for
     * THIS search (matched on searchId, so a superseded search can never own
     * the slot of the current one).
     */
    rapid?: IRapidAnswerState;
    /**
     * graft/A7 v2: the live input is overview-shaped and the endpoint is
     * available — reserve the card frame now, before any request is made.
     */
    rapidArmed?: boolean;
    /** graft/A7: open a record the overview cited (same navigation as a result click). */
    onOpenSource?: (source: IRapidAnswerSource) => void;
    /**
     * graft/A7: fired after the agent hand-off dispatches `bt:openSearchAgent`.
     * An overlay surface closes itself so the dock is not behind a backdrop;
     * the page passes nothing. Same split as AiSearchAssist.onAgentOpen.
     */
    onAgentOpen?: () => void;
}

/**
 * Renders the reserved QA slot, or nothing when no slot is owed.
 *
 * Legacy path: the slot appears the moment intent reports isQuestion (a
 * fixed-height skeleton card) and the eventual qa event replaces the skeleton
 * IN PLACE. A2-review (thread 1511890): once reserved it holds its height
 * for the rest of the search; an answerless end swaps the shimmer for a quiet
 * "no answer" state at the SAME height.
 */
export function AiSearchQASlot(props: IAiSearchQASlotProps) {
    const {
        qaAnswers,
        phase,
        isQuestion,
        hasQuery,
        searchId,
        heightPx,
        rapid,
        rapidArmed,
        onOpenSource,
        onAgentOpen,
    } = props;

    // A2-review (thread 1511891): long answers line-clamp inside the fixed
    // slot with a "Show full answer" / "Show more" toggle instead of hiding
    // overflow behind a nested scrollbar. Expansion is user-initiated (D4
    // allows it); it resets per search.
    const [qaExpanded, setQaExpanded] = useState(false);
    useEffect(() => {
        setQaExpanded(false);
    }, [searchId]);

    const askAgent = (query: string) => {
        window.dispatchEvent(new CustomEvent("bt:openSearchAgent", { detail: { query } }));
        onAgentOpen?.();
    };

    // ── graft/A7 v2: the overview owns the slot for an overview-shaped query ──
    const rapidOwned =
        rapid !== undefined && rapid.phase !== "idle" && rapid.searchId === searchId && hasQuery;
    const rapidReserved = hasQuery && (rapidOwned || rapidArmed === true);
    const outcome = rapidOwned ? rapidOutcome(rapid) : rapidReserved ? "frame" : null;

    if (outcome !== null) {
        const shown = rapidOwned ? rapid : RAPID_ANSWER_IDLE;
        const needsClamp =
            outcome === "card" &&
            shown.phase === "done" &&
            shown.text.length > RAPID_QA_CLAMP_THRESHOLD_CHARS;
        const height =
            outcome === "card" || outcome === "frame"
                ? RAPID_QA_CARD_HEIGHT_PX
                : outcome === "note"
                  ? RAPID_QA_NOTE_HEIGHT_PX
                  : 0;
        const expanded = qaExpanded && needsClamp;
        return (
            <div
                className="ai-search-qa-slot ai-search-qa-slot--rapid"
                style={expanded ? { minHeight: height } : { height, overflow: "hidden" }}
                data-testid="ai-search-qa-slot"
                data-rapid={outcome}
                aria-hidden={outcome === "gone" ? true : undefined}
            >
                <RapidAnswer
                    rapid={shown}
                    clamped={needsClamp}
                    expanded={expanded}
                    onToggleExpand={() => setQaExpanded((v) => !v)}
                    onOpenSource={onOpenSource}
                    onAskAgent={askAgent}
                />
            </div>
        );
    }

    // ── Legacy qa-event path (Rapid QA inert) ────────────────────────────
    const hasQA = Object.keys(qaAnswers).length > 0;
    const qaStreamOpen = phase === "loading" || phase === "streaming";
    const showQASlot = hasQA || (isQuestion && hasQuery && phase !== "idle");
    if (!showQASlot) return null;

    const qaNeedsClamp = Object.values(qaAnswers).some(
        (a) => a.text.length > QA_CLAMP_THRESHOLD_CHARS,
    );
    const qaSlotExpanded = qaExpanded && qaNeedsClamp;
    const slotHeight = heightPx ?? QA_SLOT_HEIGHT_PX;
    const qaSlotContent: "answer" | "skeleton" | "no-answer" = hasQA
        ? "answer"
        : qaStreamOpen
          ? "skeleton"
          : "no-answer";

    return (
        <div
            className="ai-search-qa-slot"
            style={
                qaSlotExpanded
                    ? { minHeight: slotHeight }
                    : { height: slotHeight, overflow: "hidden" }
            }
            data-testid="ai-search-qa-slot"
        >
            {qaSlotContent === "answer" && (
                <>
                    <AiSearchQA qaAnswers={qaAnswers} clamped={qaNeedsClamp && !qaExpanded} />
                    {qaNeedsClamp && (
                        <BTButton
                            type="tertiary"
                            onClick={() => setQaExpanded((v) => !v)}
                            data-testid="ai-search-qa-expand"
                            className="ai-search-qa-expand"
                        >
                            {qaExpanded ? "Show less" : "Show full answer"}
                        </BTButton>
                    )}
                </>
            )}
            {qaSlotContent === "skeleton" && <QASkeletonCard />}
            {qaSlotContent === "no-answer" && (
                <div className="ai-search-qa" data-testid="ai-search-qa-no-answer">
                    <div className="ai-search-qa-card">
                        <div className="ai-search-qa-label">AI Answer</div>
                        <div className="ai-search-qa-text ai-search-qa-text--muted">
                            No answer available for this question &mdash; the results below may
                            help.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
